import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SessionGuard, SessionTimeoutModal } from "./components/SessionGuard";
import UpgradeModal from "./components/UpgradeModal";
import TrialProtection from "./components/TrialProtection";
import TrialSurveyModal from "./components/TrialSurveyModal";
import AiLockGuard from "./components/AiLockGuard";
import AskAiPanel from "./components/AskAiPanel";
import { PreferencesProvider } from "./context/PreferencesContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Requirements from "./pages/Requirements";
import RequirementDetail from "./pages/RequirementDetail";
import Documents from "./pages/Documents";
import DocumentEditor from "./pages/DocumentEditor";
import Controls from "./pages/Controls";
import RiskMatrix from "./pages/RiskMatrix";
import BuildControls from "./pages/BuildControls";
import PolicyManuals from "./pages/PolicyManuals";
import Evidence from "./pages/Evidence";
import Rsaw from "./pages/Rsaw";
import ClaroLens from "./pages/ClaroLens";
import ClaroLensDetail from "./pages/ClaroLensDetail";
import SopForms from "./pages/SopForms";
import FormBuilder from "./pages/FormBuilder";
import Rfi from "./pages/Rfi";
import Tasks from "./pages/Tasks";
import Tracker from "./pages/Tracker";
import Prc005 from "./pages/Prc005";
import Eop004 from "./pages/Eop004";
import Eop004Event from "./pages/Eop004Event";
import ClaroFlow from "./pages/ClaroFlow";
import UserGuide from "./pages/UserGuide";
import ConsoleManuals from "./pages/ConsoleManuals";
import Sdp from "./pages/Sdp";
import SdpProject from "./pages/SdpProject";
import SdpDraft from "./pages/SdpDraft";
import Gaps from "./pages/Gaps";
import CfrMatrix from "./pages/CfrMatrix";
import Cip013Scrm from "./pages/Cip013Scrm";
import CipEvidenceRequestTool from "./pages/CipEvidenceRequestTool";
import Cip004Access from "./pages/Cip004Access";
import Cip002Assets from "./pages/Cip002Assets";
import Cip007Patch from "./pages/Cip007Patch";
import Cip007Ports from "./pages/Cip007Ports";
import Cip007Controls from "./pages/Cip007Controls";
import Cip010Baselines from "./pages/Cip010Baselines";
import Cip010Compliance from "./pages/Cip010Compliance";
import ClaroPatch from "./pages/ClaroPatch";
import StarLanding from "./pages/StarLanding";
import RiskAssessmentLanding from "./pages/RiskAssessmentLanding";
import RiskAssessmentEditor from "./pages/RiskAssessmentEditor";
import DocChecker from "./pages/DocChecker";
import DataAnalyzer from "./pages/DataAnalyzer";
import ReadinessEvidence from "./pages/ReadinessEvidence";
import RequiredDocs from "./pages/RequiredDocs";
import TransitionWorkspace from "./pages/TransitionWorkspace";
import MyAssignments from "./pages/MyAssignments";
import Notifications from "./pages/Notifications";
import Approvals from "./pages/Approvals";
import Admin from "./pages/Admin";
import ClaroConsole from "./pages/ClaroConsole";import Investigations from "./pages/Investigations";
import ConsoleClaroShield from "./pages/ConsoleClaroShield";
import InvestigationDetail from "./pages/InvestigationDetail";
import Account from "./pages/Account";
import Billing from "./pages/Billing";
import Plans from "./pages/Plans";
import NercLibrary from "./pages/NercLibrary";
import SanctionCalculator from "./pages/SanctionCalculator";
import PncTracker from "./pages/PncTracker";
import IncidentTracker from "./pages/IncidentTracker";
import ReviewSignOff from "./pages/ReviewSignOff";
import AccessRequestPublic from "./pages/AccessRequestPublic";
import EntityPicker from "./pages/EntityPicker";
import SOC2Hub from "./pages/SOC2Hub";
import Engagements from "./pages/Engagements";
import EngagementDetail from "./pages/EngagementDetail";

// True when a user just logged in and hasn't yet landed on the right entity.
// Super-admins normally skip the picker (they use ClaroConsole + the sidebar
// switcher) — BUT a pending vanity/company login (e.g. /power-electric-company)
// must route EVERYONE, including super-admins, through the picker so that the
// requested company is actually selected instead of the default entity.
function needsEntityPick(user) {
  if (!user) return false;
  let pendingOrg = false;
  try { pendingOrg = !!sessionStorage.getItem("claro-login-org"); } catch { /* noop */ }
  if (pendingOrg) return true;
  if (user.is_superadmin) return false;
  try { return sessionStorage.getItem("claro-entity-unpicked") === "1"; } catch { return false; }
}

function Protected({ children }) {
  const { user, booting } = useAuth();
  if (booting || user === null)
    return <div className="h-screen flex items-center justify-center font-mono text-sm">Loading workspace…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (needsEntityPick(user)) return <Navigate to="/select-entity" replace />;
  return <Layout>{children}</Layout>;
}

// Legacy /rapid-reviews/:id bookmarks → ClaroLens
function LegacyLensRedirect() {
  const { id } = useParams();
  return <Navigate to={`/claro-lens/${id}`} replace />;
}

// Auth-only wrapper (no Layout chrome) for the full-screen entity picker.
function Authed({ children }) {
  const { user, booting } = useAuth();
  if (booting || user === null)
    return <div className="h-screen flex items-center justify-center font-mono text-sm">Loading workspace…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <PreferencesProvider>
        <BrowserRouter>
          <Toaster position="top-right" />
          <SessionGuard />
          <SessionTimeoutModal />
          <UpgradeModal />
          <TrialProtection />
          <TrialSurveyModal />
          <AiLockGuard />
          <AskAiPanel />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/review/:token" element={<ReviewSignOff />} />
            <Route path="/access-request/:token" element={<AccessRequestPublic />} />
            <Route path="/select-entity" element={<Authed><EntityPicker /></Authed>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/requirements" element={<Protected><Requirements /></Protected>} />
            <Route path="/nerc-library" element={<Protected><NercLibrary /></Protected>} />
            <Route path="/sanction-calculator" element={<Protected><SanctionCalculator /></Protected>} />
            <Route path="/pnc-tracker" element={<Protected><PncTracker /></Protected>} />
            <Route path="/engagements" element={<Protected><Engagements /></Protected>} />
            <Route path="/engagements/:id" element={<Protected><EngagementDetail /></Protected>} />
            <Route path="/incidents" element={<Protected><IncidentTracker /></Protected>} />
            <Route path="/requirements/:id" element={<Protected><RequirementDetail /></Protected>} />
            <Route path="/documents" element={<Protected><Documents /></Protected>} />
            <Route path="/documents/:id" element={<Protected><DocumentEditor /></Protected>} />
            <Route path="/controls" element={<Protected><Controls /></Protected>} />
            <Route path="/risk-matrix" element={<Protected><RiskMatrix /></Protected>} />
            <Route path="/build" element={<Protected><BuildControls /></Protected>} />
            <Route path="/manuals" element={<Protected><PolicyManuals /></Protected>} />
            <Route path="/evidence" element={<Protected><Evidence /></Protected>} />
            <Route path="/rsaw" element={<Protected><Rsaw /></Protected>} />
            <Route path="/claro-lens" element={<Protected><ClaroLens /></Protected>} />
            <Route path="/claro-lens/:id" element={<Protected><ClaroLensDetail /></Protected>} />
            <Route path="/rapid-reviews" element={<Navigate to="/claro-lens" replace />} />
            <Route path="/rapid-reviews/:id" element={<LegacyLensRedirect />} />
            <Route path="/sop-forms" element={<Protected><SopForms /></Protected>} />
            <Route path="/forms/:id" element={<Protected><FormBuilder /></Protected>} />
            <Route path="/rfi" element={<Protected><Rfi /></Protected>} />
            <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
            <Route path="/cipwatch" element={<Protected><Tracker track="cip" /></Protected>} />
            <Route path="/opwatch" element={<Protected><Tracker track="op" /></Protected>} />
            <Route path="/ciptrack" element={<Navigate to="/cipwatch" replace />} />
            <Route path="/optrack" element={<Navigate to="/opwatch" replace />} />
            <Route path="/prc005" element={<Protected><Prc005 /></Protected>} />
            <Route path="/eop004" element={<Protected><Eop004 /></Protected>} />
            <Route path="/eop004/:id" element={<Protected><Eop004Event /></Protected>} />
            <Route path="/claroflow" element={<Protected><ClaroFlow /></Protected>} />
            <Route path="/sdp" element={<Protected><Sdp /></Protected>} />
            <Route path="/sdp/:id" element={<Protected><SdpProject /></Protected>} />
            <Route path="/sdp/draft/:did" element={<Protected><SdpDraft /></Protected>} />
            <Route path="/guide" element={<Protected><UserGuide /></Protected>} />
            <Route path="/guide/bcsi-key-setup" element={<Protected><UserGuide resourceKey="bcsi_customer" /></Protected>} />
            <Route path="/console/staff-guide" element={<Protected><UserGuide superadmin={true} /></Protected>} />
            <Route path="/console/manuals" element={<Protected><ConsoleManuals /></Protected>} />
            <Route path="/gaps" element={<Protected><Gaps /></Protected>} />
            <Route path="/cfr" element={<Protected><CfrMatrix /></Protected>} />
            <Route path="/scrm" element={<Protected><Cip013Scrm /></Protected>} />
            <Route path="/ert" element={<Protected><CipEvidenceRequestTool /></Protected>} />
            <Route path="/cip004" element={<Protected><Cip004Access /></Protected>} />
            <Route path="/cip002" element={<Protected><Cip002Assets /></Protected>} />
            <Route path="/cip007" element={<Protected><Cip007Patch /></Protected>} />
            <Route path="/cip007-ports" element={<Protected><Cip007Ports /></Protected>} />
            <Route path="/cip007-controls" element={<Protected><Cip007Controls /></Protected>} />
            <Route path="/cip010" element={<Protected><Cip010Baselines /></Protected>} />
            <Route path="/cip010-monitoring" element={<Protected><Cip010Compliance /></Protected>} />
            <Route path="/claropatch" element={<Protected><ClaroPatch /></Protected>} />
            <Route path="/star" element={<Protected><StarLanding /></Protected>} />
            <Route path="/risk-assessments" element={<Protected><RiskAssessmentLanding /></Protected>} />
            <Route path="/risk-assessments/:id" element={<Protected><RiskAssessmentEditor /></Protected>} />
            <Route path="/doc-checker" element={<Protected><DocChecker /></Protected>} />
            <Route path="/data-analyzer" element={<Protected><DataAnalyzer /></Protected>} />
            <Route path="/readiness-evidence" element={<Protected><ReadinessEvidence /></Protected>} />
            <Route path="/required-docs" element={<Protected><RequiredDocs /></Protected>} />
            <Route path="/transitions/:id" element={<Protected><TransitionWorkspace /></Protected>} />
            <Route path="/my-assignments" element={<Protected><MyAssignments /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
            <Route path="/admin" element={<Protected><Admin /></Protected>} />
            <Route path="/console" element={<Protected><ClaroConsole /></Protected>} />
            <Route path="/console/soc2" element={<Protected><SOC2Hub /></Protected>} />
            <Route path="/console/claro-shield" element={<Protected><ConsoleClaroShield /></Protected>} />
            <Route path="/investigations" element={<Protected><Investigations /></Protected>} />
            <Route path="/investigations/:id" element={<Protected><InvestigationDetail /></Protected>} />
            <Route path="/account" element={<Protected><Account /></Protected>} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/plans" element={<Protected><Plans /></Protected>} />
            <Route path="/:orgSlug" element={<Login />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </PreferencesProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
